/**
 * Modelo de conteúdo das Novidades (as atualizações publicadas pelo almode-novidades), lido no ERP.
 *
 * É puro: não usa DOM, Vue nem rede. A conversão do markdown em HTML fica em `renderizar.ts`.
 *
 * O conteúdo real não segue um formato só. Das atualizações publicadas até a REL-ALM0048:
 * - o grupo aparece como "## Funcionalidades", "## **Funcionalidade**", "## **Novas Funcionalidades**",
 *   "## Melhorias", "## **Melhoria**" ou "## Melhorias do Sistema", e às vezes nem aparece;
 * - o item fica em "##" ou em "###", com emoji, com `**` e às vezes numerado ("1.", "**2.");
 * - nas que numeram os itens, um "## Como funciona?" sem número é subtítulo, não item;
 * - a REL-ALM0047 não tem "##": os itens são linhas em CAIXA ALTA que começam com emoji.
 */

export const MESES = [
	'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
	'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

/** Como a API do almode-novidades devolve cada atualização (GET /novidades). */
export interface NovidadeDaApi {
	id: string
	title?: string
	desc?: string
	content?: string
	categories?: string[]
	/** Formato antigo, de uma área só. */
	category?: string
	year: string | number
	month: string
	monthOrder: string | number
	dateLabel?: string
	createdAt?: string
}

export type GrupoDoItem = 'funcionalidade' | 'melhoria' | null

export interface ItemDaAtualizacao {
	/** Número do item na atualização inteira, a partir de 1 (os grupos não reiniciam a contagem). */
	ordem: number
	/** Sem emoji, numeração nem marcação: índice, capa, linha do tempo e busca. */
	titulo: string
	/** Com o emoji do autor, sem numeração nem marcação: cabeçalho do cartão na leitura. */
	tituloDeLeitura: string
	grupo: GrupoDoItem
	/** Markdown do item sem a linha do título; qualquer subtítulo vira "####". */
	corpo: string
	imagens: string[]
	textoDeBusca: string
}

export interface Atualizacao {
	id: string
	mes: string
	ano: number
	numeroDoMes: number
	/** "2026-10": ordena e agrupa por mês. */
	chaveDoMes: string
	/** "Outubro de 2026". */
	periodo: string
	areas: string[]
	resumo: string
	/** Markdown que vem antes do primeiro grupo ou item. */
	introducao: string
	itens: ItemDaAtualizacao[]
	funcionalidades: number
	melhorias: number
	imagens: string[]
	/** 1 para a mais antiga do mês. */
	ordemNoMes: number
	totalNoMes: number
	textoDeBusca: string
}

export interface MesDaLinhaDoTempo {
	chave: string
	mes: string
	ano: number
	atualizacoes: Atualizacao[]
}

export interface AnoDaLinhaDoTempo {
	ano: number
	meses: MesDaLinhaDoTempo[]
}

export interface GrupoDoIndice {
	nome: string
	grupo: GrupoDoItem
	itens: ItemDaAtualizacao[]
}

export interface ResultadoDaBusca {
	encontrou: boolean
	/** Ordem dos itens em que todas as palavras aparecem. */
	itens: number[]
}

const EMOJI = /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|[\u{1F3FB}-\u{1F3FF}]|️|‍|⃣|�/gu
const COMECA_COM_EMOJI = /^\p{Extended_Pictographic}/u
const GRUPO = /^(novas?\s+)?(funcionalidades?|melhorias?)(\s+do\s+sistema)?$/i
const TITULO = /^(#{1,6})\s+(.*?)\s*#*\s*$/
const IMAGEM = /!\[[^\]]*\]\(\s*<?([^)\s>]+)>?(?:\s+["'][^"']*["'])?\s*\)|<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["']/gi
const SIGLAS: { padrao: RegExp; escrita: string }[] = [
	'NFC-e', 'NF-e', 'CC-e', 'CPF', 'CNPJ', 'PDV', 'CSV', 'XML', 'API', 'CRM', 'TEF', 'Procon',
].map(escrita => ({ padrao: new RegExp(`\\b${escrita}\\b`, 'gi'), escrita }))

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`

function semMarcacao(texto: string) {
	return texto
		.replace(/<[^>]+>/g, '')
		.replace(/\*\*|__|\\|`/g, '')
}

function semNumeracao(texto: string) {
	return texto.replace(/^\s*\d+\s*[.)]\s*/, '')
}

function ehNumerado(texto: string) {
	return /^\d+\s*[.)]/.test(semMarcacao(texto).replace(EMOJI, '').trim())
}

/** Título para índice, capa e busca: sem emoji, numeração, marcação nem pontuação no fim. */
export function limparTitulo(texto: string): string {
	return semNumeracao(semMarcacao(texto || '').replace(EMOJI, ' '))
		.replace(/\s+/g, ' ')
		.replace(/^[\s:.\-–—]+|[\s:!.\-–—]+$/g, '')
		.trim()
}

function tituloDeLeitura(texto: string) {
	return semNumeracao(semMarcacao(texto).replace(/�/g, '').trim())
		.replace(/\s+/g, ' ')
		.trim()
}

/** Linha em CAIXA ALTA vira frase, sem perder as siglas. */
function frase(texto: string) {
	let t = texto.toLowerCase()
		.replace(/\p{L}/u, c => c.toUpperCase())
		.replace(/\((\p{L})/gu, (_, c) => '(' + c.toUpperCase())
	for (const { padrao, escrita } of SIGLAS) t = t.replace(padrao, escrita)
	return t
}

/**
 * Corrige o que o markdown não entende e dá títulos ao conteúdo antigo que não tem nenhum
 * (REL-ALM0047): linha curta que começa com emoji e vem depois de uma linha vazia vira item, se estiver
 * em CAIXA ALTA, ou subtítulo, se não estiver.
 */
export function normalizarConteudo(markdown: string): string {
	const linhas = (markdown || '')
		.replace(/\r\n?/g, '\n')
		.split('\n')
		.map(l => l.replace(/^(\s*)•\s*/, '$1- '))
	if (linhas.length && /^#\s/.test(linhas[0])) linhas.shift()
	if (linhas.some(l => /^#{2,3}\s/.test(l))) return linhas.join('\n')

	return linhas.map((linha, i) => {
		const t = linha.trim()
		const vazioAntes = i === 0 || !linhas[i - 1].trim()
		if (!vazioAntes || !COMECA_COM_EMOJI.test(t) || t.length >= 90 || /[.:]$/.test(t)) return linha
		const letras = t.replace(/[^\p{L}]/gu, '')
		const maiusculas = letras.replace(/[^\p{Lu}]/gu, '')
		const caixaAlta = letras.length > 0 && maiusculas.length / letras.length > 0.7
		return caixaAlta ? '## ' + frase(t) : '#### ' + t
	}).join('\n')
}

/** Endereços das imagens, na ordem em que aparecem. */
export function imagensDoMarkdown(markdown: string): string[] {
	const imagens: string[] = []
	for (const m of (markdown || '').matchAll(IMAGEM)) imagens.push(m[1] || m[2])
	return imagens
}

/** Texto de busca: sem acento, sem marcação, em minúsculas. */
export function normalizarParaBusca(texto: string): string {
	return (texto || '')
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim()
}

function textoPlano(markdown: string) {
	return (markdown || '')
		.replace(IMAGEM, ' ')
		.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/<[^>]+>/g, ' ')
		.replace(/[#*_`>~|]/g, ' ')
}

function rebaixarTitulos(linhas: string[]) {
	return linhas.map(l => {
		const m = TITULO.exec(l)
		return m ? '#### ' + m[2] : l
	})
}

interface Titulo {
	linha: number
	nivel: number
	texto: string
	grupo: GrupoDoItem | undefined
}

/** Separa a introdução e os itens, com o grupo de cada item. */
export function separarItens(markdown: string): { introducao: string; itens: ItemDaAtualizacao[] } {
	const linhas = markdown.split('\n')
	const titulos: Titulo[] = []
	linhas.forEach((linha, i) => {
		const m = TITULO.exec(linha)
		if (!m || m[1].length === 1) return
		const limpo = limparTitulo(m[2])
		const grupo = GRUPO.test(limpo)
			? (/melhoria/i.test(limpo) ? 'melhoria' : 'funcionalidade')
			: undefined
		titulos.push({ linha: i, nivel: m[1].length, texto: m[2], grupo })
	})

	const grupos = titulos.filter(t => t.grupo !== undefined)
	const candidatos = titulos.filter(t => t.grupo === undefined && t.nivel <= 3 && limparTitulo(t.texto))
	const nivelDoItem = Math.min(...candidatos.map(t => t.nivel))
	let doItem = candidatos.filter(t => t.nivel === nivelDoItem)
	const numerados = doItem.filter(t => ehNumerado(t.texto))
	if (numerados.length >= 2 && numerados.length < doItem.length && numerados.length * 2 >= doItem.length) {
		doItem = numerados
	}

	const fronteiras = [...doItem, ...grupos].map(t => t.linha).sort((a, b) => a - b)
	const proximaFronteira = (linha: number) => fronteiras.find(f => f > linha) ?? linhas.length
	const inicio = fronteiras.length ? fronteiras[0] : linhas.length
	const introducao = rebaixarTitulos(linhas.slice(0, inicio)).join('\n').trim()

	const itens = doItem.map((t, i): ItemDaAtualizacao => {
		const ultimoGrupo = grupos.filter(g => g.linha < t.linha).pop()
		const corpo = rebaixarTitulos(linhas.slice(t.linha + 1, proximaFronteira(t.linha))).join('\n').trim()
		const titulo = limparTitulo(t.texto)
		return {
			ordem: i + 1,
			titulo,
			tituloDeLeitura: tituloDeLeitura(t.texto),
			grupo: ultimoGrupo ? ultimoGrupo.grupo as GrupoDoItem : null,
			corpo,
			imagens: imagensDoMarkdown(corpo),
			textoDeBusca: normalizarParaBusca(titulo + ' ' + textoPlano(corpo)),
		}
	})
	return { introducao, itens }
}

function numeroDoMes(r: NovidadeDaApi) {
	const n = Number(r.monthOrder)
	if (n >= 1 && n <= 12) return n
	return MESES.findIndex(m => normalizarParaBusca(m) === normalizarParaBusca(r.month)) + 1
}

/** Número do código (REL-ALM0048 dá 48): desempata as atualizações do mesmo mês. */
export function numeroDoCodigo(id: string): number {
	const m = /(\d+)\D*$/.exec(id || '')
	return m ? Number(m[1]) : 0
}

export function montarAtualizacao(r: NovidadeDaApi): Atualizacao {
	const conteudo = normalizarConteudo(r.content || r.desc || '')
	const { introducao, itens } = separarItens(conteudo)
	const ano = Number(r.year)
	const numero = numeroDoMes(r)
	const mes = MESES[numero - 1] || r.month
	const periodo = `${mes} de ${ano}`
	const areas = r.categories ?? (r.category ? [r.category] : [])
	const resumo = limparTitulo((r.desc || '').split(/\n\s*\n/)[0])
	return {
		id: r.id,
		mes,
		ano,
		numeroDoMes: numero,
		chaveDoMes: `${ano}-${String(numero).padStart(2, '0')}`,
		periodo,
		areas,
		resumo,
		introducao,
		itens,
		funcionalidades: itens.filter(i => i.grupo === 'funcionalidade').length,
		melhorias: itens.filter(i => i.grupo === 'melhoria').length,
		imagens: imagensDoMarkdown(conteudo),
		ordemNoMes: 1,
		totalNoMes: 1,
		textoDeBusca: normalizarParaBusca([
			periodo, r.id, ...areas, resumo, textoPlano(introducao),
			...itens.map(i => i.textoDeBusca),
		].join(' ')),
	}
}

/**
 * Mais recente primeiro. A API ordena por ano e mês sem desempate, então duas atualizações do mesmo
 * mês podem vir trocadas: o desempate é o número do código. A mais antiga do mês é a 1ª.
 */
export function ordenarAtualizacoes(lista: Atualizacao[]): Atualizacao[] {
	const ordenada = [...lista].sort((a, b) =>
		b.chaveDoMes.localeCompare(a.chaveDoMes) || numeroDoCodigo(b.id) - numeroDoCodigo(a.id))
	const porMes = new Map<string, Atualizacao[]>()
	for (const a of ordenada) porMes.set(a.chaveDoMes, [...(porMes.get(a.chaveDoMes) || []), a])
	return ordenada.map(a => {
		const doMes = porMes.get(a.chaveDoMes) || [a]
		return { ...a, totalNoMes: doMes.length, ordemNoMes: doMes.length - doMes.indexOf(a) }
	})
}

export function carregarAtualizacoes(daApi: NovidadeDaApi[]): Atualizacao[] {
	return ordenarAtualizacoes((daApi || []).filter(r => r && r.id).map(montarAtualizacao))
}

/** Linha do tempo: ano, depois mês, na ordem da lista (que já vem do mais recente). */
export function agruparPorAnoEMes(lista: Atualizacao[]): AnoDaLinhaDoTempo[] {
	const anos: AnoDaLinhaDoTempo[] = []
	for (const a of lista) {
		let ano = anos[anos.length - 1]
		if (!ano || ano.ano !== a.ano) anos.push(ano = { ano: a.ano, meses: [] })
		let mes = ano.meses[ano.meses.length - 1]
		if (!mes || mes.chave !== a.chaveDoMes) ano.meses.push(mes = { chave: a.chaveDoMes, mes: a.mes, ano: a.ano, atualizacoes: [] })
		mes.atualizacoes.push(a)
	}
	return anos
}

/** "29 novidades: 15 funcionalidades e 14 melhorias", "1 novidade". Vazio quando não há item. */
export function contagem(a: Atualizacao): string {
	const n = a.itens.length
	if (!n) return ''
	const total = plural(n, 'novidade', 'novidades')
	if (!a.funcionalidades || !a.melhorias) return total
	return `${total}: ${plural(a.funcionalidades, 'funcionalidade', 'funcionalidades')} e ${plural(a.melhorias, 'melhoria', 'melhorias')}`
}

/** "e mais 23 itens" depois dos que já apareceram; vazio quando todos já apareceram. */
export function itensRestantes(a: Atualizacao, mostrados: number): string {
	const n = a.itens.length - mostrados
	return n > 0 ? `e mais ${plural(n, 'item', 'itens')}` : ''
}

/** "2ª atualização do mês", só quando o mês tem mais de uma. */
export function parteDoMes(a: Atualizacao): string {
	return a.totalNoMes > 1 ? `${a.ordemNoMes}ª atualização do mês` : ''
}

export function rotuloDoBotaoDeLeitura(a: Atualizacao): string {
	const mes = a.mes.toLowerCase()
	return a.totalNoMes > 1
		? `Ler a ${a.ordemNoMes}ª atualização de ${mes}`
		: `Ler a atualização de ${mes}`
}

/** Todas as palavras precisam aparecer, sem diferenciar acento nem maiúscula. */
export function buscar(a: Atualizacao, termo: string): ResultadoDaBusca {
	const palavras = normalizarParaBusca(termo).split(' ').filter(Boolean)
	if (!palavras.length) return { encontrou: true, itens: [] }
	const tem = (texto: string) => palavras.every(p => texto.includes(p))
	const itens = a.itens.filter(i => tem(i.textoDeBusca)).map(i => i.ordem)
	return { encontrou: itens.length > 0 || tem(a.textoDeBusca), itens }
}

export interface ImagemDoMosaico {
	src: string
	item: number
	titulo: string
}

/** Primeira imagem de cada item, na ordem, até o limite. Sem imagem, a capa fica só com texto. */
export function mosaico(a: Atualizacao, limite = 3): ImagemDoMosaico[] {
	return a.itens
		.filter(i => i.imagens.length > 0)
		.slice(0, limite)
		.map(i => ({ src: i.imagens[0], item: i.ordem, titulo: i.titulo }))
}

export interface ImagemDaLeitura {
	src: string
	item: number
	titulo: string
}

/** Todas as imagens da atualização, na ordem, com o item de cada uma: navegação do zoom. */
export function imagensDaLeitura(a: Atualizacao): ImagemDaLeitura[] {
	return a.itens.flatMap(i => i.imagens.map(src => ({ src, item: i.ordem, titulo: i.titulo })))
}

const NOME_DO_GRUPO: Record<string, string> = {
	funcionalidade: 'Funcionalidades',
	melhoria: 'Melhorias',
}

/** Grupos na ordem do conteúdo; itens sem grupo ficam em "Nesta atualização". */
export function gruposDoIndice(a: Atualizacao): GrupoDoIndice[] {
	const grupos: GrupoDoIndice[] = []
	for (const item of a.itens) {
		let g = grupos[grupos.length - 1]
		if (!g || g.grupo !== item.grupo) {
			grupos.push(g = { nome: NOME_DO_GRUPO[item.grupo || ''] || 'Nesta atualização', grupo: item.grupo, itens: [] })
		}
		g.itens.push(item)
	}
	return grupos
}

/** Anterior é a mais antiga seguinte; próxima é a mais nova. A lista vem do mais recente. */
export function vizinhas(lista: Atualizacao[], id: string): { anterior: Atualizacao | null; proxima: Atualizacao | null } {
	const i = lista.findIndex(a => a.id === id)
	if (i < 0) return { anterior: null, proxima: null }
	return { anterior: lista[i + 1] || null, proxima: lista[i - 1] || null }
}
